import { z } from 'zod';
/**
 * List Superadmin Recording Analytics Request Schema
 * GET /api/v2/superadmin-recording-analytics
 *
 * Optional `tenantId` switches the response between two drill-down levels:
 * omitted → one rollup row per tenant; set → one row per process (with its
 * steps) inside that tenant.
 */ export const ListSuperadminRecordingAnalyticsRequestSchema = z.object({
    tenantId: z.string().optional()
});
/**
 * A single step recording within a process. `name` is resolved server-side via
 * the `generatedName → task.taskDescription → recordingId` fallback chain.
 */ export const RecordingStepSchema = z.object({
    recordingId: z.string(),
    name: z.string(),
    activeRecordingMs: z.number()
});
/**
 * One process inside the selected tenant, with its measured step recordings.
 * `steps` is sorted descending by `activeRecordingMs`. Only steps with a
 * measured `activeRecordingMs > 0` are included.
 */ export const RecordingProcessRowSchema = z.object({
    processId: z.string(),
    processName: z.string(),
    stepCount: z.number(),
    totalActiveRecordingMs: z.number(),
    avgPerStepMs: z.number(),
    steps: z.array(RecordingStepSchema)
});
/**
 * Per-tenant rollup row for the top-level overview. Counts and totals span all
 * measured step recordings across the tenant's processes.
 */ export const RecordingTenantRowSchema = z.object({
    tenantId: z.string(),
    tenantName: z.string(),
    processCount: z.number(),
    stepCount: z.number(),
    avgPerStepMs: z.number(),
    avgPerProcessMs: z.number(),
    totalActiveRecordingMs: z.number()
});
/**
 * Top-level aggregates over the in-scope (optionally tenant-filtered) measured
 * recordings. Both averages divide the same aggregated active time by a
 * different count: per-step over every step recording, per-process over the
 * distinct processes those recordings belong to. `0` when no measured data is
 * in scope.
 */ export const RecordingAnalyticsSummarySchema = z.object({
    totalActiveRecordingMs: z.number(),
    recordingCount: z.number(),
    processCount: z.number(),
    avgPerStepMs: z.number(),
    avgPerProcessMs: z.number()
});
/**
 * The response carries a `summary` scoped to the current selection plus exactly
 * one populated drill-down array: `tenants` when no `tenantId` was given,
 * `processes` when one was. The other array is empty.
 */ export const ListSuperadminRecordingAnalyticsResponseSchema = z.object({
    summary: RecordingAnalyticsSummarySchema,
    tenants: z.array(RecordingTenantRowSchema),
    processes: z.array(RecordingProcessRowSchema)
});

//# sourceMappingURL=list-superadmin-recording-analytics.schema.js.map